import { useEffect, useRef, useCallback } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";

const HEARTBEAT_INTERVAL_MS = 60_000;
const ACTIVITY_THROTTLE_MS = 5_000;

export function useHeartbeat() {
  const { user, logout, twoFactorPending } = useAuth();
  const { toast } = useToast();
  const lastActivityRef = useRef<number>(Date.now());
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningShownRef = useRef(false);
  const isLoggingOutRef = useRef(false);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (warningShownRef.current) {
      warningShownRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    lastActivityRef.current = Date.now();
    isLoggingOutRef.current = false;

    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    let throttled = false;

    const handleActivity = () => {
      if (throttled) return;
      throttled = true;
      updateActivity();
      throttleTimer = setTimeout(() => { throttled = false; }, ACTIVITY_THROTTLE_MS);
    };

    // Declared before handleVisibilityChange so it can be called from there.
    const sendHeartbeat = async () => {
      if (isLoggingOutRef.current) return;
      try {
        const res = await apiRequest("POST", "/api/agent-heartbeat", {
          lastActivityTimestamp: lastActivityRef.current,
          tabVisible: document.visibilityState === "visible",
        });
        const data = await res.json();

        if (data.action === "logout") {
          isLoggingOutRef.current = true;
          toast({
            title: "Session Expired",
            description: "You have been logged out due to inactivity.",
            variant: "destructive",
          });
          setTimeout(() => logout(), 1000);
        } else if (data.action === "warn" && !warningShownRef.current) {
          warningShownRef.current = true;
          const minutes = Math.ceil((data.remainingSeconds || 300) / 60);
          toast({
            title: "Inactivity Warning",
            description: `You will be logged out in ${minutes} minute${minutes !== 1 ? "s" : ""} due to inactivity. Move your mouse or press a key to stay logged in.`,
            duration: 30000,
          });
        }
      } catch (err: any) {
        if (err?.message?.includes("401") || err?.message?.includes("Not authenticated")) {
          // Never auto-logout while a 2FA verification is in progress —
          // the session is legitimately unauthenticated until the code is entered.
          if (twoFactorPending) return;
          isLoggingOutRef.current = true;
          logout();
        }
      }
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("touchstart", handleActivity);
    window.addEventListener("scroll", handleActivity, true);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updateActivity();
        // Fire a heartbeat immediately when the tab becomes visible (e.g. laptop
        // lid opened). Without this the expired session isn't detected until the
        // next 60-second interval tick, leaving the app on the stale logged-in
        // state for up to a minute before redirecting to login.
        sendHeartbeat();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    sendHeartbeat();
    heartbeatTimerRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      window.removeEventListener("scroll", handleActivity, true);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (throttleTimer) clearTimeout(throttleTimer);
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
  }, [user, logout, toast, updateActivity, twoFactorPending]);
}
